import { Injectable } from '@angular/core';
import posthog, { PostHogConfig } from 'posthog-js';

/**
 * Singleton wrapper around posthog-js for the ADMIN app (live monitoring +
 * product analytics for the internal admin tool).
 *
 * Differs from the public app's PosthogService on purpose:
 *  - No SSR guard — the admin is a plain SPA (no Angular Universal), so there
 *    is no server render path to short-circuit.
 *  - No cookie-consent gate — the admin is an authenticated internal tool for
 *    staff, not a public site under GDPR opt-in. It initialises directly.
 *
 * What it keeps from the public pattern (the load-bearing parts):
 *  - init() is idempotent (only the first call wires posthog.init).
 *  - Every SDK call is wrapped in try/catch — a PostHog failure (network
 *    blocked, SDK invariant) must NEVER throw into admin business code or
 *    break a screen. Analytics off ≠ admin broken.
 */
@Injectable({ providedIn: 'root' })
export class PosthogService {
    private initialized = false;

    /**
     * Initialise posthog-js. Idempotent — safe to call more than once.
     * Called once at AppModule construction.
     */
    init(apiKey: string, options: Partial<PostHogConfig>): void {
        if (this.initialized) return;
        if (!apiKey) return;
        try {
            posthog.init(apiKey, options);
            this.initialized = true;
        } catch (err) {
            // Never let analytics init crash the admin shell.
            console.warn('[posthog] init failed:', err);
        }
    }

    /** Send a tracked event. Swallows any SDK error. */
    capture(eventName: string, props?: Record<string, unknown>): void {
        if (!this.initialized) return;
        try {
            posthog.capture(eventName, props);
        } catch (err) {
            console.warn('[posthog] capture failed:', err);
        }
    }

    /** Identify the signed-in admin user. Same error-swallowing contract. */
    identify(distinctId: string, props?: Record<string, unknown>): void {
        if (!this.initialized || !distinctId) return;
        try {
            posthog.identify(distinctId, props);
        } catch (err) {
            console.warn('[posthog] identify failed:', err);
        }
    }

    /** Clear the identified user (on logout). */
    reset(): void {
        if (!this.initialized) return;
        try {
            posthog.reset();
        } catch (err) {
            console.warn('[posthog] reset failed:', err);
        }
    }

    /** Diagnostic: has the SDK initialised? Pure read; never throws. */
    isInitialized(): boolean {
        return this.initialized;
    }
}
